// Driver dashboard's vehicle-check countdown/lockout status.
//
// DRIVER-scoped, not vehicle-scoped: looks at when this driver last
// completed ANY vehicle inspection, not "has today's rostered vehicle been
// checked". Per Janelle (2026-09): drivers move between vehicles through
// the day/week, so tying the lockout to one specific vehicle_number meant
// a driver who'd genuinely checked a vehicle that morning could still show
// as locked out the moment they switched to a different one -- e.g.
// inspecting 441 for one job, then moving into their rostered 453, with
// the old vehicle-scoped lockout only ever looking at 453's own (much
// older) last check.
//
// This is deliberately separate from computeVehicleCheckDue
// (src/lib/vehicleCheckDue.ts), which stays vehicle-scoped -- that's
// VehicleInspection.tsx's own "is THIS vehicle due" signal (used when
// picking which vehicle to inspect), a genuinely different question from
// "is this driver locked out of the rest of the app".
//
// Lockout is deliberately DAYS-ONLY (not the day-OR-shift-count "due"
// logic VehicleInspection.tsx itself uses) -- per Janelle: keep the
// dashboard countdown simple and literal (0/3 the day of a check, 3/3 =
// locked), the shift-count trigger stays a VehicleInspection-only signal.
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

const SETTINGS_INTERVAL_DAYS_NAME = "fleet_vehicle_check_interval_days";
const SETTINGS_LOCKOUT_MESSAGE_NAME = "fleet_vehicle_check_lockout_message";

export const DEFAULT_LOCKOUT_MESSAGE =
  "Your vehicle check is now overdue. Complete a Vehicle Check to continue — other tabs, including submitting completed jobs for payment, stay locked until this is done.";

export interface DriverVehicleCheckWorst {
  daysSinceLastCheck: number | null;
  intervalDays: number;
}

// isExempt (drivers.exempt_from_inspection) skips the lockout entirely --
// for admins who don't drive fleet vehicles day to day, per Janelle.
export function useDriverVehicleCheckStatus(driverName: string, isExempt = false) {
  const [loading, setLoading] = useState(false);
  const [worst, setWorst] = useState<DriverVehicleCheckWorst | null>(null);
  const [lockoutMessage, setLockoutMessage] = useState(DEFAULT_LOCKOUT_MESSAGE);

  const load = useCallback(async () => {
    if (!driverName || isExempt) {
      setWorst(null);
      return;
    }
    setLoading(true);
    try {
      const { data: settingRows } = await supabase
        .from("settings")
        .select("name, value")
        .in("name", [SETTINGS_INTERVAL_DAYS_NAME, SETTINGS_LOCKOUT_MESSAGE_NAME]);

      let intervalDays = 3;
      const intervalRow = settingRows?.find((s) => s.name === SETTINGS_INTERVAL_DAYS_NAME);
      if (intervalRow?.value) {
        const n = parseInt(String(intervalRow.value), 10);
        if (!Number.isNaN(n) && n > 0) intervalDays = n;
      }
      const messageRow = settingRows?.find((s) => s.name === SETTINGS_LOCKOUT_MESSAGE_NAME);
      setLockoutMessage(messageRow?.value?.trim() || DEFAULT_LOCKOUT_MESSAGE);

      // This driver's own most recent COMPLETED inspection, any vehicle --
      // completed_at, not created_at, so opening the wizard and abandoning
      // it (no checklist, no photos) doesn't unlock anything. See
      // vehicle_inspections.completed_at.
      const { data: lastInspection } = await supabase
        .from("vehicle_inspections")
        .select("completed_at")
        .eq("driver_name", driverName)
        .not("completed_at", "is", null)
        .order("completed_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      const daysSinceLastCheck = lastInspection?.completed_at
        ? Math.floor((Date.now() - new Date(lastInspection.completed_at).getTime()) / (1000 * 60 * 60 * 24))
        : null;

      setWorst({ daysSinceLastCheck, intervalDays });
    } finally {
      setLoading(false);
    }
  }, [driverName, isExempt]);

  useEffect(() => {
    load();
  }, [load]);

  const isLockedOut =
    !!worst && (worst.daysSinceLastCheck === null || worst.daysSinceLastCheck >= worst.intervalDays);

  return { loading, worst, isLockedOut, lockoutMessage, refetch: load };
}
